import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api.js';

export type AdminProvider = {
  id: string;
  name: string;
  shortName: string | null;
  cricosProviderCode: string | null;
  rtoCode: string | null;
  tgaReference: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  primaryState: string | null;
  campuses: Array<{ name: string; state?: string | null; address?: string | null }>;
  contactEmail: string | null;
  curationState: 'shell' | 'curated' | 'needs_review' | 'archived';
  lastVerifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateProviderPayload = Partial<Omit<AdminProvider, 'id' | 'createdAt' | 'updatedAt' | 'lastVerifiedAt'>> & {
  name: string;
};

export function useAdminProviders(q?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admin', 'providers', q ?? ''],
    queryFn: () => {
      const query = q ? `?q=${encodeURIComponent(q)}` : '';
      return apiGet<{ items: AdminProvider[] }>(`/admin/providers${query}`);
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useAdminProvider(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'providers', 'detail', id],
    queryFn: () => apiGet<{ provider: AdminProvider }>(`/admin/providers/${id}`),
    enabled: !!id,
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProviderPayload) =>
      apiPost<{ provider: AdminProvider }>(`/admin/providers`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

export function useUpdateProvider(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateProviderPayload>) =>
      apiPatch<{ provider: AdminProvider }>(`/admin/providers/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/admin/providers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    },
  });
}
